import React, { Fragment, useEffect, useState } from "react";
import UserMaterialOpportunitiesNPITableRow from "./UserMaterialOpportunitiesNPITableRow/UserMaterialOpportunitiesNPITableRow";
import i18next from "../../i18n";
import { Button, Loader, Modal, Pagination, Slide } from "..";
import { useLanguage } from "../../hooks";
import { OpportunityByFamilyResponse } from "../../types";
import { usePublicCountries } from "../../hooks/useCountries";
import { useCreateMaterialOfferList } from "../../hooks/useMaterialsOffers";
import { useNotification, useUser } from "../../context";
import { useMaterialsFromFamily } from "../../hooks/useMaterialOpportunities";
import { useNavigate } from "react-router-dom";

const tableTh = [
  { id: 1, name: i18next.t("material.productClass") },
  { id: 2, name: i18next.t("material.merchancyCode") },
  { id: 3, name: i18next.t("navigation.opportunities") },
  { id: 4, name: "" },
];

type UserMaterialOpportunitiesNPITableProps = {
  familyPage: number;
  setFamilyPage: (page: number) => void;
  npiOpportunities: OpportunityByFamilyResponse[];
  pagination: any;
  countryIds: string[];
};

const UserMaterialOpportunitiesNPITable = ({
  familyPage,
  setFamilyPage,
  npiOpportunities,
  pagination,
  countryIds,
}: UserMaterialOpportunitiesNPITableProps) => {
  const { openNotification } = useNotification();
  const { user } = useUser();
  const navigate = useNavigate();
  const langEs = useLanguage();
  const [materialOffers, setMaterialOffers] = useState<any[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<
    {
      materialId: string;
      companyId: string;
    }[]
  >([]);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [origin, setOrigin] = useState("");
  const [materialsPage, setMaterialsPage] = useState(0);

  const { data: countries } = usePublicCountries();

  const {
    mutate: createMaterialOfferList,
    isPending: isCreatingMaterialOfferList,
  } = useCreateMaterialOfferList(
    () => {
      setOpen(false);
      openNotification(i18next.t("materialOffer.success"));
      navigate("/material/offers");
    },
    () => openNotification(i18next.t("newMaterialForm.error"))
  );

  const {
    data: materials,
    mutate: getMaterialsFromFamily,
    isPending,
  } = useMaterialsFromFamily();

  const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (currentCategoryId !== null) {
      getMaterialsFromFamily({
        categoryId: currentCategoryId,
        countryIds,
        page: materialsPage,
      });
    }
  }, [materialsPage, currentCategoryId, countryIds, getMaterialsFromFamily]);

  const handleCategoryClick = (categoryId: string) => {
    setCurrentCategoryId(categoryId);
    setMaterialsPage(0);
    getMaterialsFromFamily({ categoryId, countryIds, page: 0 });
  };

  useEffect(() => {
    if (!open) {
      setMaterialOffers([]);
      setSelectedMaterials([]);
    }
  }, [open]);

  useEffect(() => {
    setMaterialOffers(
      selectedMaterials.map(({ materialId, companyId }) => ({
        specificationFileUrl: "",
        material: { id: materialId },
        origin: { id: origin },
        user: { id: user?.id },
        companyId,
        comment: "",
      }))
    );
  }, [selectedMaterials, origin, user]);

  const handleOriginChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMaterialOffers((prevState) => {
      return prevState.map((offer) => {
        return {
          ...offer,
          origin: { id: e.target.value },
        };
      });
    });
    setOrigin(e.target.value);
  };

  const handleMaterialCheckbox = (materialId: string, companyId: string) => {
    setSelectedMaterials((prev) => {
      const exists = prev.find((item) => item.materialId === materialId);
      if (exists) {
        return prev.filter((item) => item.materialId !== materialId);
      } else {
        return [...prev, { materialId, companyId }];
      }
    });
  };

  const handleOpen = () => setOpenModal(true);

  const handleSubmit = () => {
    setOpenModal(false);
    const uniqueByCompany = Array.from(
      new Map(materialOffers.map((offer) => [offer.companyId, offer])).values()
    );
    createMaterialOfferList(uniqueByCompany);
  };

  return (
    <>
      <Modal
        title={i18next.t("materialOffer.apply")}
        open={openModal}
        setOpen={setOpenModal}
      >
        <p className="text-xs text-gray-600">
          {i18next.t("materialOffer.applyQuestion")}
        </p>
        <div className="flex justify-end mt-4">
          <Button onClick={handleSubmit}>
            {i18next.t("materialOffer.apply")}
          </Button>
        </div>
      </Modal>
      <div className="mt-4">
        <div className="flex flex-col mt-2">
          <div className="align-middle min-w-full overflow-x-auto shadow overflow-hidden sm:rounded">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {tableTh?.map((th) => (
                    <th
                      key={th.id}
                      scope="col"
                      className="px-2 py-2 text-left text-xxs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {th.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {npiOpportunities?.map(({ quantity, category }) => (
                  <UserMaterialOpportunitiesNPITableRow
                    key={category.id}
                    quantity={quantity}
                    category={category}
                    handleClick={handleCategoryClick}
                    setOpen={setOpen}
                  />
                ))}
              </tbody>
            </table>
            <Pagination
              results={pagination.numberOfElements}
              total={pagination.totalElements}
              amountOfPages={pagination.totalPages}
              currentPage={familyPage}
              setPageNumber={setFamilyPage}
            />
          </div>
        </div>
      </div>
      <Slide open={open} setOpen={setOpen}>
        {isCreatingMaterialOfferList ? (
          <Loader />
        ) : (
          <>
            <h4 className="text-spectum text-xs font-bold uppercase tracking-wide my-3">
              {i18next.t("opportunities.npiOpportunities")}
            </h4>
            <div className="max-h-500 overflow-y-scroll py-2">
              {isPending ? (
                <Loader />
              ) : (
                <>
                  {materials?.content && materials.content.length > 0 && (
                    <>
                      {materials.content.map(
                        ({ id, material, country, companyId }) => (
                          <Fragment key={id}>
                            <legend className="sr-only">{material}</legend>
                            <div className="flex items-center gap-2 mb-2">
                              <input
                                type="checkbox"
                                checked={selectedMaterials.some(
                                  (item) => item.materialId === id
                                )}
                                onChange={() =>
                                  handleMaterialCheckbox(id, companyId)
                                }
                                className="ml-2 rounded-md"
                                id={id}
                              />
                              <div className="relative flex items-center">
                                <div className="text-xs">
                                  <label htmlFor={id} className="text-gray-700">
                                    <span className="text-gray-900 uppercase">
                                      {material}
                                    </span>{" "}
                                    -{" "}
                                    <span className="text-gray-700">
                                      {country?.name}
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </Fragment>
                        )
                      )}
                    </>
                  )}
                </>
              )}
              {materials && (
                <Pagination
                  results={materials?.numberOfElements}
                  total={materials?.totalElements}
                  amountOfPages={materials?.totalPages}
                  currentPage={materialsPage}
                  setPageNumber={setMaterialsPage}
                />
              )}
            </div>
            {materialOffers.length > 0 && (
              <div className="mt-4">
                <label
                  htmlFor="origin"
                  className="uppercase block text-xs font-medium text-gray-700"
                >
                  {i18next.t("materialOffer.origins")}
                  <span className="text-sm text-red-600"> *</span>
                </label>
                <select
                  id="origin"
                  name="origin"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-xs rounded"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleOriginChange(e)
                  }
                >
                  <option value="">{i18next.t("materialOffer.select")}</option>
                  {countries?.map((country) => (
                    <option key={country.id} value={country.id}>
                      {langEs ? country.name : country.nameEn}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex mt-5 justify-end">
              <button
                onClick={handleOpen}
                disabled={!(materialOffers.length > 0 && origin)}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-xs font-medium rounded ${
                  !(materialOffers.length > 0 && origin)
                    ? "bg-white text-gray-400"
                    : "bg-spectum hover:bg-spectum-light text-white"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-spectum-light uppercase`}
              >
                {i18next.t("materialOffer.cta")}
              </button>
            </div>
          </>
        )}
      </Slide>
    </>
  );
};

export default UserMaterialOpportunitiesNPITable;
